import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { MapPin, X, Compass, Navigation, LocateFixed } from "lucide-react";
import "leaflet/dist/leaflet.css";

/* -----------------------------------------------------------
   CarteJeu — Carte interactive OpenStreetMap
   Position joueur (géoloc) + marqueurs étapes color-codés
   selon leur statut (ouverte / en cours / verrouillée).
   ----------------------------------------------------------- */

/** Marqueur SVG inline, couleur & état contextualisés (thème "Aventure & Temps"). */
function makeEtapeIcon({ color, ordre, done }) {
  const bg = color;
  const inner = done ? "✓" : ordre;
  return L.divIcon({
    className: "",
    iconSize: [36, 46],
    iconAnchor: [18, 44],
    popupAnchor: [0, -40],
    html: `
      <div style="
        width:36px;height:46px;position:relative;
        filter: drop-shadow(0 4px 8px rgba(0,0,0,0.5));
      ">
        <svg viewBox="0 0 36 46" width="36" height="46" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 0 C7 0 0 8 0 18 C0 30 18 46 18 46 C18 46 36 30 36 18 C36 8 29 0 18 0 Z"
                fill="${bg}" stroke="#0C0A08" stroke-width="1.5"/>
          <circle cx="18" cy="18" r="10" fill="#0C0A08" opacity="0.85"/>
        </svg>
        <div style="
          position:absolute;left:0;top:9px;width:36px;text-align:center;
          font-family: 'DM Mono', ui-monospace, monospace;
          font-size: 14px; font-weight: 700; color:${bg};
          line-height: 18px; letter-spacing: 0.02em;
        ">${inner}</div>
      </div>`,
  });
}

/** Marqueur mystère (indice du Sage) : "?" clignotant doré. */
const mysteryIcon = L.divIcon({
  className: "",
  iconSize: [38, 48],
  iconAnchor: [19, 46],
  html: `
    <div style="width:38px;height:48px;position:relative;
                animation: sageBlink 1.6s ease-in-out infinite;
                filter: drop-shadow(0 4px 12px rgba(200,146,42,0.7));">
      <svg viewBox="0 0 36 46" width="38" height="48" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 0 C7 0 0 8 0 18 C0 30 18 46 18 46 C18 46 36 30 36 18 C36 8 29 0 18 0 Z"
              fill="#C8922A" stroke="#0C0A08" stroke-width="1.5"/>
        <circle cx="18" cy="18" r="10" fill="#0C0A08" opacity="0.9"/>
      </svg>
      <div style="
        position:absolute;left:0;top:6px;width:38px;text-align:center;
        font-family: 'Playfair Display', ui-serif, serif;
        font-size: 22px; font-weight: 700; color:#C8922A;
        line-height: 22px; font-style: italic;
      ">?</div>
    </div>
    <style>
      @keyframes sageBlink {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.15); opacity: 0.55; }
      }
    </style>`,
});

/** Marqueur joueur : point pulsant bleu. */
const playerIcon = L.divIcon({
  className: "",
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  html: `
    <div style="width:26px;height:26px;position:relative;">
      <div style="
        position:absolute;inset:0;border-radius:50%;
        background:#4A9B6E;opacity:0.35;
        animation: pulseRing 2.4s cubic-bezier(0.22,1,0.36,1) infinite;
      "></div>
      <div style="
        position:absolute;inset:6px;border-radius:50%;
        background:#4A9B6E;border:2px solid #F5EFE6;
        box-shadow:0 2px 6px rgba(0,0,0,0.4);
      "></div>
    </div>
    <style>
      @keyframes pulseRing {
        0% { transform: scale(0.6); opacity: 0.6; }
        100% { transform: scale(2); opacity: 0; }
      }
    </style>`,
});

/** Sous-composant : ajuste automatiquement le zoom pour englober tous les points. */
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
  }, [points, map]);
  return null;
}

export default function CarteJeu({ etapes = [], cadenasOuverts, pistesActives = [] }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { lat, lng, accuracy }
  const [locState, setLocState] = useState("idle"); // idle | locating | ok | denied

  /* -- Géolocalisation continue (seulement quand la carte est ouverte) -- */
  useEffect(() => {
    if (!open) return;
    if (!("geolocation" in navigator)) {
      setLocState("denied");
      return;
    }
    setLocState("locating");
    const id = navigator.geolocation.watchPosition(
      (p) => {
        setPos({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: p.coords.accuracy,
        });
        setLocState("ok");
      },
      () => setLocState("denied"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [open]);

  /* -- Étapes géolocalisées + leur statut visuel -- */
  const etapesRevealed = useMemo(
    () =>
      (etapes || [])
        .filter((e) => typeof e.lat === "number" && typeof e.lng === "number")
        .map((e) => {
          const enigmes = e.enigmes || [];
          const totalCadenas = enigmes.length;
          const ouverts = enigmes.filter((eg) =>
            cadenasOuverts.has(eg.numeroCadenas)
          ).length;
          const done = totalCadenas > 0 && ouverts === totalCadenas;
          return { ...e, totalCadenas, ouverts, done };
        })
        // Brouillard d'aventure : on ne révèle que les étapes déjà conquises
        .filter((e) => e.done),
    [etapes, cadenasOuverts]
  );

  /* -- Étapes mystères (indice du Sage) : ni révélées, ni complètes -- */
  const mysteryEtapes = useMemo(() => {
    if (!pistesActives?.length) return [];
    const idsMystere = new Set();
    pistesActives.forEach((p) => {
      if (p.reelId) idsMystere.add(p.reelId);
      if (p.leurreId) idsMystere.add(p.leurreId);
    });
    return (etapes || [])
      .filter((e) => idsMystere.has(e.id))
      .filter((e) => typeof e.lat === "number" && typeof e.lng === "number")
      .map((e) => {
        const enigmes = e.enigmes || [];
        const done =
          enigmes.length > 0 &&
          enigmes.every((eg) => cadenasOuverts.has(eg.numeroCadenas));
        return { ...e, done };
      })
      // On n'affiche en "mystère" que celles pas encore résolues (sinon elles sont déjà en vert)
      .filter((e) => !e.done);
  }, [etapes, cadenasOuverts, pistesActives]);

  const points = useMemo(() => {
    const pts = etapesRevealed.map((e) => [e.lat, e.lng]);
    mysteryEtapes.forEach((e) => pts.push([e.lat, e.lng]));
    if (pos) pts.push([pos.lat, pos.lng]);
    return pts;
  }, [etapesRevealed, mysteryEtapes, pos]);

  const nbRevealed = etapesRevealed.length;
  const nbMysteres = mysteryEtapes.length;
  // Nombre total d'étapes géolocalisées (pour l'empty state / le compteur)
  const nbGeoloc = useMemo(
    () =>
      (etapes || []).filter(
        (e) => typeof e.lat === "number" && typeof e.lng === "number"
      ).length,
    [etapes]
  );

  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={() => setOpen(true)}
        data-testid="carte-toggle"
        aria-label="Ouvrir la carte des lieux découverts"
        disabled={nbGeoloc === 0}
        className={`fixed bottom-4 left-4 z-40 flex items-center gap-2 px-4 py-3 rounded-sm shadow-[0_10px_40px_-10px_rgba(74,155,110,0.6)] transition-all ${
          nbGeoloc === 0
            ? "bg-noir-100 text-parchemin/30 border border-or/10 cursor-not-allowed"
            : "bg-vert border border-vert-50/60 text-parchemin hover:bg-vert-900"
        }`}
      >
        <MapPin className="w-4 h-4 text-vert-50" strokeWidth={2} />
        <span className="text-sm font-mono uppercase tracking-widest">Carte</span>
        {(nbRevealed > 0 || nbMysteres > 0) && (
          <span className="font-mono text-xs bg-noir text-vert-50 px-2 py-0.5 rounded-sm">
            {nbRevealed}
            {nbMysteres > 0 && (
              <span className="text-or ml-1">+{nbMysteres}?</span>
            )}
          </span>
        )}
      </button>

      {/* Modal plein écran */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-noir flex flex-col"
          data-testid="carte-modal"
        >
          {/* Header modal */}
          <header className="flex items-center justify-between gap-3 px-4 py-3 bg-noir-100/95 backdrop-blur border-b border-or/25 z-10">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-sm bg-vert-900 border border-vert-50/40 flex items-center justify-center">
                <Compass className="w-4 h-4 text-vert-50" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-vert-50/80">
                  Le territoire
                </div>
                <h2 className="font-display text-base text-parchemin leading-tight truncate">
                  Carte des étapes
                </h2>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="carte-close"
              aria-label="Fermer la carte"
              className="w-9 h-9 flex items-center justify-center border border-or/30 hover:border-or hover:bg-or/5 text-or rounded-sm transition"
            >
              <X className="w-4 h-4" />
            </button>
          </header>

          {/* Bandeau statut géoloc */}
          <div
            className="px-4 py-2 flex items-center gap-2 text-[11px] font-mono tracking-wide text-parchemin/70 bg-noir-100/70 border-b border-or/10"
            data-testid="carte-geoloc-status"
          >
            {locState === "locating" && (
              <>
                <LocateFixed className="w-3.5 h-3.5 text-or animate-pulse" />
                Recherche du signal GPS…
              </>
            )}
            {locState === "ok" && pos && (
              <>
                <Navigation className="w-3.5 h-3.5 text-vert-50" />
                Position détectée · précision ±{Math.round(pos.accuracy)}&nbsp;m
              </>
            )}
            {locState === "denied" && (
              <>
                <Navigation className="w-3.5 h-3.5 text-oxyde" />
                GPS refusé — les lieux découverts restent visibles
              </>
            )}
            {locState === "idle" && (
              <>
                <Navigation className="w-3.5 h-3.5 text-parchemin/40" />
                {nbRevealed} lieu{nbRevealed > 1 ? "x" : ""} découvert
                {nbRevealed > 1 ? "s" : ""} sur {nbGeoloc}
              </>
            )}
          </div>

          {/* Carte */}
          <div className="flex-1 relative">
            {nbRevealed === 0 && nbMysteres === 0 ? (
              <div
                className="absolute inset-0 flex items-center justify-center text-center px-6 bg-noir"
                data-testid="carte-empty"
              >
                <div className="max-w-sm">
                  <Compass className="w-10 h-10 text-or/60 mx-auto mb-4 animate-glow-pulse" />
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-or/70 mb-2">
                    Brouillard d&apos;aventure
                  </div>
                  <h3 className="font-display text-xl text-parchemin mb-3">
                    Rien n&apos;est <span className="italic text-or">encore révélé.</span>
                  </h3>
                  <p className="text-parchemin/60 text-sm leading-relaxed">
                    La carte se dévoile au fil de vos découvertes. Résolvez les
                    énigmes pour marquer chaque lieu conquis, ou consultez Le
                    Sage du Causse pour obtenir un indice mystère.
                  </p>
                  <p className="mt-3 font-mono text-[10px] tracking-wider uppercase text-parchemin/40">
                    {nbGeoloc} lieu{nbGeoloc > 1 ? "x" : ""} à trouver
                  </p>
                </div>
              </div>
            ) : (
              <MapContainer
                center={
                  etapesRevealed[0]
                    ? [etapesRevealed[0].lat, etapesRevealed[0].lng]
                    : [mysteryEtapes[0].lat, mysteryEtapes[0].lng]
                }
                zoom={14}
                scrollWheelZoom
                style={{ height: "100%", width: "100%", background: "#0C0A08" }}
                data-testid="leaflet-map"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds points={points} />

                {etapesRevealed.map((e) => (
                  <Marker
                    key={e.id}
                    position={[e.lat, e.lng]}
                    icon={makeEtapeIcon({
                      color: "#4A9B6E",
                      ordre: e.ordre,
                      done: true,
                    })}
                  >
                    <Popup>
                      <div style={{ minWidth: 180 }}>
                        <div
                          style={{
                            fontFamily: "'DM Mono', monospace",
                            fontSize: 10,
                            letterSpacing: "0.2em",
                            textTransform: "uppercase",
                            color: "#4A9B6E",
                          }}
                        >
                          Lieu conquis
                        </div>
                        <div
                          style={{
                            fontFamily: "'Playfair Display', serif",
                            fontSize: 16,
                            marginTop: 4,
                            color: "#0C0A08",
                          }}
                        >
                          {e.emoji || "📍"} {e.nom}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#555",
                            marginTop: 4,
                          }}
                        >
                          Cadenas ouverts&nbsp;: {e.ouverts}/{e.totalCadenas}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {/* Marqueurs mystères (indice du Sage) — ni nom ni info spoiler */}
                {mysteryEtapes.map((e) => (
                  <Marker
                    key={`mystery-${e.id}`}
                    position={[e.lat, e.lng]}
                    icon={mysteryIcon}
                    zIndexOffset={500}
                    data-testid="marker-mystere"
                  >
                    <Popup>
                      <div style={{ minWidth: 160 }}>
                        <div
                          style={{
                            fontFamily: "'DM Mono', monospace",
                            fontSize: 10,
                            letterSpacing: "0.2em",
                            textTransform: "uppercase",
                            color: "#C8922A",
                          }}
                        >
                          Indice du Sage
                        </div>
                        <div
                          style={{
                            fontFamily: "'Playfair Display', serif",
                            fontStyle: "italic",
                            fontSize: 15,
                            marginTop: 4,
                            color: "#0C0A08",
                          }}
                        >
                          Un lieu à explorer.<br />
                          À toi de deviner lequel est le bon.
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {pos && (
                  <Marker
                    position={[pos.lat, pos.lng]}
                    icon={playerIcon}
                    zIndexOffset={1000}
                    data-testid="marker-joueur"
                  >
                    <Popup>
                      <div style={{ minWidth: 140 }}>
                        <div
                          style={{
                            fontFamily: "'DM Mono', monospace",
                            fontSize: 10,
                            letterSpacing: "0.2em",
                            textTransform: "uppercase",
                            color: "#4A9B6E",
                          }}
                        >
                          Vous êtes ici
                        </div>
                        <div
                          style={{
                            fontFamily: "'Playfair Display', serif",
                            fontSize: 14,
                            marginTop: 4,
                            color: "#0C0A08",
                          }}
                        >
                          Précision ±{Math.round(pos.accuracy)}&nbsp;m
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            )}
          </div>

          {/* Légende */}
          {(nbRevealed > 0 || nbMysteres > 0) && (
            <div
              className="px-4 py-3 bg-noir-100/95 backdrop-blur border-t border-or/15 flex items-center justify-center gap-5 text-[10px] font-mono uppercase tracking-widest text-parchemin/70 flex-wrap"
              data-testid="carte-legend"
            >
              <LegendDot color="#4A9B6E" label="Conquis" />
              {nbMysteres > 0 && (
                <LegendDot color="#C8922A" label="Indice du Sage ?" />
              )}
              <div className="flex items-center gap-1.5">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full border-2 border-parchemin"
                  style={{ background: "#4A9B6E" }}
                />
                Vous
              </div>
              <div className="text-parchemin/40 italic normal-case font-display tracking-normal">
                · {nbGeoloc - nbRevealed} à découvrir
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="inline-block w-2.5 h-2.5 rounded-full"
        style={{ background: color }}
      />
      {label}
    </div>
  );
}
